import {
    Context,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';

type IntersectionContextType = {
    // NULL has a special meaning, it means the document viewport (see IntersectionObserver docs)
    rootEl: HTMLElement | null | undefined;
};

/**
 * The factory methods below let us create multiple IntersectionContexts with
 * unique names.  This way components can register to the context they need
 * rather than just the next-closest provider.
 */

const ctxRegistry = new Map<
    string,
    Context<IntersectionContextType | undefined>
>();

const createIntersectionContext = (name: string) => {
    const context = createContext<IntersectionContextType | undefined>(
        undefined
    );
    ctxRegistry.set(name, context);
    return {
        context,
        Provider: context.Provider,
    };
};

const getIntersectionContext = (name: string) => {
    const context = ctxRegistry.get(name);
    if (context) {
        return {
            context,
            Provider: context.Provider,
        };
    } else {
        return undefined;
    }
};

export const createOrGetIntersectionContext = (name: string) => {
    const context = getIntersectionContext(name);
    if (context) {
        return context;
    } else {
        return createIntersectionContext(name);
    }
};

export const destroyIntersectionContext = (name: string) => {
    ctxRegistry.delete(name);
};

type UseIntersectionContextOptions = {
    silenceMissingProviderError?: boolean;
};

const EMPTY_CONTEXT: IntersectionContextType = {
    rootEl: null,
};

/**
 * Access the context at the registered name.
 *
 * If the downstream code can gracefully handle the missing provider then
 * silenceMissingProviderError can be set to true.
 *
 * @param name
 * @param param1
 * @returns
 */
export const useIntersectionContext = (
    name: string,
    options?: UseIntersectionContextOptions
): IntersectionContextType => {
    const ctx = getIntersectionContext(name);
    const { silenceMissingProviderError = false } = options ?? {};

    if (!ctx) {
        if (silenceMissingProviderError) {
            return EMPTY_CONTEXT;
        }
        throw new Error(`Intersection context "${name}" does not exist`);
    }

    const context = useContext(ctx.context);

    if (!context) {
        if (silenceMissingProviderError) {
            return EMPTY_CONTEXT;
        }
        throw new Error(
            `useIntersectionContext must be used within a Provider matching the name "${name}"`
        );
    }

    return context;
};

export type UseIntersectionTargetCallback = (target: Element) => void;

export type UseIntersectionTargetOptions = {
    rootMargin?: string;
    threshold?: number;
    silenceMissingProviderError?: boolean;
};

/**
 * Register an intersection target with the context matching contextName.
 * When the target is intersected, onIntersection will be called with the
 * target element.
 *
 * Options:
 * - rootMargin: margin around the root element to use for intersection (see IntersectionObserver docs)
 * - threshold: threshold for intersection (see IntersectionObserver docs)
 * - silenceMissingProviderError: if true, will not throw an error if the context is not found
 *   meaning that if not context is set up we'll make the intersection a no-op rather than an error
 */
export const useIntersectionTarget = (
    contextName: string,
    onIntersection: UseIntersectionTargetCallback,
    onIntersectionEnd?: (target: Element) => void,
    options?: UseIntersectionTargetOptions
) => {
    const {
        rootMargin = '0px',
        threshold = 1,
        silenceMissingProviderError = true,
    } = options ?? {};

    const { rootEl } = useIntersectionContext(contextName, {
        silenceMissingProviderError,
    });

    const [, setTarget] = useState<HTMLDivElement | null>(null);
    const [observer, setObserver] = useState<IntersectionObserver | null>(null);
    const [reset, setReset] = useState(0);

    useEffect(() => {
        // unregister existing observers when rootEl changes
        if (observer) {
            observer.disconnect();
        }

        // register new observer if rootEl is available
        if (rootEl !== undefined) {
            const newObserver = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting) {
                        onIntersection(entries[0].target);
                    } else {
                        if (onIntersectionEnd) {
                            onIntersectionEnd(entries[0].target);
                        }
                    }
                },
                {
                    root: rootEl,
                    rootMargin,
                    threshold,
                }
            );
            setObserver(newObserver);
        } else {
            setObserver(null);
        }
    }, [rootEl, reset]);

    const targetRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (observer) {
                setTarget((prev) => {
                    if (prev) {
                        observer.unobserve(prev);
                    }
                    if (node) {
                        observer.observe(node);
                    }
                    return node;
                });
            }
        },
        [observer]
    );

    return { targetRef, reset: () => setReset((prev) => prev + 1) };
};
